import React from 'react';
import { Helmet } from 'react-helmet';
import AboutNav from '../Components/Navigation/AboutNav';
import Footer from '../Components/Home/Footer';
import SEBISlider from '../Components/Common/SEBISlider';
import ISOCertificateButton from '../Components/Common/ISOCertificateButton';
import { FaFileContract, FaShieldAlt, FaExclamationTriangle, FaUserShield, FaLock, FaHandshake, FaInfoCircle, FaGavel } from 'react-icons/fa';
import '../Assets/Styles/Pages/terms_page.css';

const Terms_page = () => {
  const termsSections = [
    {
      icon: <FaFileContract />,
      title: "Website Access Agreement",
      content: "By accessing and using this website, you agree to be bound by the following terms and conditions."
    },
    {
      icon: <FaInfoCircle />,
      title: "Free Trial Recommendations",
      content: "The free trial recommendations provided by Trillion Stock Research are intended solely for demonstration purposes. The decision to act upon or disregard these recommendations rests entirely with you."
    },
    {
      icon: <FaShieldAlt />,
      title: "Research Recommendations",
      content: "Our research recommendations are based on the package you select. Our research analysts conduct comprehensive analysis to generate these recommendations. However, the final decision to follow or avoid these recommendations is yours. Trillion Stock Research will not be held liable for any gains or losses resulting from your decisions based on our recommendations."
    },
    {
      icon: <FaExclamationTriangle />,
      title: "Limitation of Liability",
      content: "Trillion Stock Research does not accept responsibility for any losses arising from market fluctuations, delays in receiving recommendations, technical issues, or any inaccuracies in the information provided. Please be aware that investing and trading in the stock market involves inherent risks, and we are not liable for any losses or damages resulting from these activities."
    },
    {
      icon: <FaLock />,
      title: "Confidentiality & Payments",
      content: "All employees, associates, and clients of Trillion Stock Research are strictly prohibited from sharing our research reports or any confidential information. Legal action will be pursued in cases of non-compliance with this policy. No employee or associate of Trillion Stock Research is permitted to accept gifts or personal benefits from clients. All payments should be made exclusively to the official account listed on our website, and not to any individual account."
    },
    {
      icon: <FaUserShield />,
      title: "Account Security",
      content: "We do not manage Demat or trading accounts. Therefore, you should never share your account credentials, including user ID, password, security question answers, or OTPs, with any of our staff members. We will not be held responsible for any issues arising from the sharing of such details."
    },
    {
      icon: <FaGavel />,
      title: "Terms Modification",
      content: "Trillion Stock Research reserves the right to modify these terms and conditions at any time, without prior notice. Any changes will become effective immediately upon being posted on the website."
    },
    {
      icon: <FaHandshake />,
      title: "User Acknowledgment",
      content: "By using this website, you acknowledge that you have read, understood, and agreed to the terms of this disclaimer. Trading in the stock market is inherently risky, and you accept full responsibility for the outcomes of your trading decisions, including any potential loss of capital. The information and recommendations provided on this website are intended for informational purposes only and should not be considered as financial advice or a solicitation to buy or sell any securities."
    },
    {
      icon: <FaShieldAlt />,
      title: "Content Usage",
      content: "All content, research, and recommendations provided on this website are for personal use only. Reproduction or distribution of any content from this site without prior written consent from Trillion Stock Research is strictly prohibited."
    },
    {
      icon: <FaHandshake />,
      title: "Final Agreement",
      content: "By continuing to use this website, you confirm your acceptance of these terms and conditions."
    }
  ];

  return (
    <>
      <Helmet>
        <title>Terms & Conditions - Trillion Stock Research | Legal Terms & User Agreement</title>
        <meta name="description" content="Read Trillion Stock Research's comprehensive terms and conditions. Understand our legal terms, user agreements, and service conditions for investment advisory services. SEBI compliant terms." />
        <meta name="keywords" content="terms and conditions, legal terms, user agreement, Trillion Stock Research, investment advisory terms, SEBI compliance terms" />
        <meta name="author" content="Trillion Stock Research" />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href="https://www.trillionstockresearch.com/terms" />
        
        {/* Open Graph Meta Tags */}
        <meta property="og:title" content="Terms & Conditions - Trillion Stock Research" />
        <meta property="og:description" content="Legal terms and conditions for Trillion Stock Research investment advisory services." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://www.trillionstockresearch.com/terms" />
        <meta property="og:image" content="https://www.trillionstockresearch.com/fav.png" />
        <meta property="og:site_name" content="Trillion Stock Research" />
        
        {/* Twitter Card Meta Tags */}
        <meta name="twitter:card" content="summary" />
        <meta name="twitter:title" content="Terms & Conditions - Trillion Stock Research" />
        <meta name="twitter:description" content="Legal terms and conditions for investment advisory services." />
        
        {/* Schema Markup */}
        <script type="application/ld+json">{`
          {
            "@context": "https://schema.org",
            "@type": "WebPage",
            "name": "Terms & Conditions",
            "description": "Legal terms and conditions for Trillion Stock Research services",
            "url": "https://www.trillionstockresearch.com/terms",
            "isPartOf": {
              "@type": "WebSite",
              "name": "Trillion Stock Research",
              "url": "https://www.trillionstockresearch.com"
            },
            "mainEntity": {
              "@type": "Article",
              "name": "Terms & Conditions",
              "description": "Legal terms and conditions for using Trillion Stock Research investment advisory services"
            }
          }
        `}</script>
      </Helmet>
      
      <div className="terms-page">
        <AboutNav />
        <SEBISlider />
        
        <section className="terms-hero">
          <div className="terms-hero-content">
            <h1>Terms & Conditions</h1>
            <p>Important legal terms and conditions for using our services</p>
          </div>
        </section>

        <section className="terms-container">
          <div className="terms-intro">
            <h2>Terms & Conditions Overview</h2>
            <p>Please read these terms and conditions carefully before using our services. By accessing and using this website, you acknowledge that you have read, understood, and agreed to be bound by these terms.</p>
          </div>

          <div className="terms-grid">
            {termsSections.map((section, index) => (
              <div key={index} className="terms-card">
                <div className="terms-icon">
                  {section.icon}
                </div>
                <h3>{section.title}</h3>
                <p className="terms-content">{section.content}</p>
              </div>
            ))}
          </div>

          <div className="terms-important">
            <h2>Important Notice</h2>
            <p>These terms and conditions constitute a legally binding agreement between you and Trillion Stock Research. Please ensure you have read and understood all terms before using our services.</p>
          </div>

          <div className="terms-update">
            <h2>Last Updated</h2>
            <p>These terms and conditions are regularly reviewed and updated to ensure compliance with the latest regulations and legal requirements. The last update was made on March 15, 2024.</p>
          </div>
        </section>

        <section className="terms-cta">
          <div className="cta-content">
            <h2>Have Questions About Our Terms?</h2>
            <p>Our legal team is here to help you understand our terms and conditions.</p>
            <button className="cta-button">Contact Legal Support</button>
          </div>
        </section>

        {/* Related Pages Section for SEO */}
        <section className="related-pages">
          <div className="related-pages-container">
            <h2>Related Legal Pages</h2>
            <div className="related-links">
              <a href="/disclaimer" className="related-link">Disclaimer</a>
              <a href="/policy" className="related-link">Privacy Policy</a>
              <a href="/refund" className="related-link">Refund Policy</a>
              <a href="/disclosure" className="related-link">SEBI Disclosure</a>
              <a href="/contact" className="related-link">Contact Us</a>
            </div>
          </div>
        </section>

        <Footer />
        <ISOCertificateButton />
      </div>
    </>
  );
};

export default Terms_page;
